import React from 'react';
import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import clsx from '@strava/ui/clsx';
import { Markup } from 'interweave';

import RemovePromoIcon from '../RemovePromoIcon/RemovePromoIcon';
import { getStyles as _getStyles } from '../../../../utils/checkoutUtils.js';
import { useCheckoutContext } from '../../../../state/CheckoutContext';
import { I18nPrefixOrganic } from '../../../../shared/constants';

import styles from './Discounts.scss';

const renderPerMonthPrice = (price, currency, inParentheses) => (
  <Markup
    tagName="span"
    content={I18n.t(
      `${I18nPrefixOrganic}.per_month${inParentheses ? '_in_parentheses' : ''}`,
      {
        class: 'price',
        price: Cldr.formatCurrency(price, currency)
      }
    )}
  />
);

const renderPerYearPrice = (price, currency, inParentheses) => (
  <Markup
    tagName="span"
    content={I18n.t(
      `${I18nPrefixOrganic}.per_year${inParentheses ? '_in_parentheses' : ''}`,
      {
        class: 'price',
        price: Cldr.formatCurrency(price, currency)
      }
    )}
  />
);

const getDiscountItemText = (discount) =>
  `${I18n.t(discount.nameKey)} (-${Math.round(discount.percent)}%)`;

export const Discounts = () => {
  const { checkoutEntityState } = useCheckoutContext();

  const {
    duration: durationFromState,
    experiments,
    products
  } = checkoutEntityState;
  const cartData = products?.[durationFromState];
  const {
    billedToday: totalPrimaryPrice,
    billedTodayOther: totalSecondaryPrice,
    currency,
    discounts,
    duration,
    monthlyTotal,
    yearlyTotal
  } = cartData;
  const promotionApplied = !!products?.promotion?.code;
  const { shouldShowCheckoutRefresh } = experiments;
  const isYearlyDuration = duration === 'yearly';

  const getStyles = (style) =>
    _getStyles(styles, style, shouldShowCheckoutRefresh);

  const totalPriceText = () => {
    if (shouldShowCheckoutRefresh) {
      return I18n.t(
        `${I18nPrefixOrganic}.${
          isYearlyDuration ? 'annual_plan' : 'monthly_plan'
        }`
      );
    }
    return I18n.t(`${I18nPrefixOrganic}.subtotal`);
  };

  const totalPrimaryText = isYearlyDuration
    ? renderPerYearPrice(totalPrimaryPrice, currency)
    : renderPerMonthPrice(totalPrimaryPrice, currency);

  const totalSecondaryTextInParentheses = isYearlyDuration
    ? renderPerMonthPrice(totalSecondaryPrice, currency, true)
    : renderPerYearPrice(totalSecondaryPrice, currency, true);

  const subtotalText = isYearlyDuration
    ? renderPerYearPrice(yearlyTotal, currency)
    : renderPerMonthPrice(monthlyTotal, currency);

  return (
    <div className={clsx(getStyles('discounts'))}>
      <div className={clsx(getStyles('summaryRow'))}>
        <div>{totalPriceText()}</div>
        <div className={clsx(getStyles('priceText'))}>{subtotalText}</div>
      </div>
      {discounts.map((discount) => (
        <div className={clsx(getStyles('summaryRow'))} key={discount.nameKey}>
          <div className={styles.centerVertically}>
            {getDiscountItemText(discount)}
            {promotionApplied && shouldShowCheckoutRefresh && (
              <RemovePromoIcon />
            )}
          </div>
          <div className={clsx(getStyles('priceText'))}>
            {isYearlyDuration
              ? renderPerYearPrice(-discount.amount, currency)
              : renderPerMonthPrice(-discount.amount, currency)}
          </div>
        </div>
      ))}

      <div className={clsx(getStyles('divider'))} />

      {shouldShowCheckoutRefresh && (
        <div className={clsx(getStyles('totals'))}>
          <div>{I18n.t(`${I18nPrefixOrganic}.total`)}</div>
          <div className="text-right">
            <div data-testid="price-list-total-primary-text-discounts">
              {totalPrimaryText}
            </div>
            <div
              className={clsx(getStyles('totalSecondaryText'))}
              data-testid="price-list-total-secondary-text-discounts"
            >
              {totalSecondaryTextInParentheses}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discounts;
